import type { ShadowLocation } from "../../types/location-map";

import { CreateFloorForm } from "../forms/create-floor-form";
import { CreateInstallationForm } from "../forms/create-installation-form";
import { CreateSensorForm } from "../forms/create-sensor-form";
import { CreateZoneForm } from "../forms/create-zone-form";
import { LocationForm } from "../forms/location-form";

export type CreateEntityModalProps = {
	/** Pre-select zone when opening create-sensor from a zone row. */
	defaultZoneId?: null | string;
	/**
	 * Selected location. Required whenever type is installation / floor / zone / sensor —
	 * those create actions are only enabled with a location selected.
	 */
	location?: null | ShadowLocation;
	mapCenter?: [number, number] | null;
	onClose: () => void;
	onLocationCreated?: (locationId: string) => void;
	type: CreateEntityType | null;
};

export type CreateEntityType =
	| "floor"
	| "installation"
	| "location"
	| "sensor"
	| "zone";

/**
 * Single create entrypoint: mounts the matching entity form dialog by `type`.
 * Each form owns its own GeneralModal chrome.
 */
export const CreateEntityModal = ({
	defaultZoneId,
	location,
	mapCenter,
	onClose,
	onLocationCreated,
	type,
}: CreateEntityModalProps) => {
	switch (type) {
		case "floor":
			if (!location) return null;
			return (
				<CreateFloorForm
					floors={location.floors}
					locationId={location.id}
					locationName={location.name}
					onClose={onClose}
				/>
			);
		case "installation":
			if (!location) return null;
			return (
				<CreateInstallationForm
					locationId={location.id}
					locationName={location.name}
					onClose={onClose}
				/>
			);
		case "location":
			return (
				<LocationForm
					mapCenter={mapCenter}
					mode="create"
					onClose={onClose}
					onCreated={onLocationCreated}
				/>
			);
		case null:
			return null;
		case "sensor":
			if (!location) return null;
			return (
				<CreateSensorForm
					defaultZoneId={defaultZoneId ?? undefined}
					location={location}
					mapCenter={mapCenter}
					onClose={onClose}
				/>
			);
		case "zone":
			if (!location) return null;
			return (
				<CreateZoneForm
					floors={location.floors}
					locationId={location.id}
					locationName={location.name}
					onClose={onClose}
					zones={location.zones}
				/>
			);
		default:
			return (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-primary/80 p-4"
					role="alert"
				>
					<div className="max-w-md rounded-lg border border-secondary bg-primary p-4 text-sm shadow-lg">
						<p className="font-medium text-primary">
							Cannot open create dialog
						</p>
						<p className="mt-1 text-tertiary">
							Unexpected create type “{String(type)}”. Expected location,
							installation, floor, zone, or sensor.
						</p>
						<button
							className="text-brand mt-3 text-sm font-medium hover:underline"
							onClick={onClose}
							type="button"
						>
							Close
						</button>
					</div>
				</div>
			);
	}
};
